/**
 * My Notifications — the one view that both reads and WRITES real data today.
 *
 * It carries the warning the deployment work surfaced: an administrator who has never signed
 * in is not mentionable, because the worker reads a cached admin flag that only a real login
 * refreshes. Someone could link Discord, never sign in, and silently never be paged. A linked
 * account shown without that caveat would imply paging works when it does not.
 */
import { useEffect, useState } from 'react';
import { SignInNotice } from './Console';
import { BASE, getPreferences, savePreferences, unlinkDiscord, type ApiFailure, type Preferences } from '../api';
import { Reading } from '../Reading';

export function MyNotifications() {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    const r = await getPreferences();
    if (r.ok) { setPrefs(r.data); setFailure(null); } else { setFailure(r.failure); }
  }

  async function patch(change: Partial<Preferences>) {
    if (!prefs) return;
    setSaving(true); setSaved(null);
    const r = await savePreferences({ ...change, version: prefs.version });
    setSaving(false);
    if (r.ok) { setPrefs(r.data); setSaved('Saved'); return; }
    if (r.failure.kind === 'conflict') {
      // Two tabs, or two people. Never merged silently.
      setPrefs(r.failure.current as Preferences);
      setSaved('Someone else changed these settings. Showing the current values — reapply your change.');
      return;
    }
    setFailure(r.failure);
  }

  if (failure) return <Failure failure={failure} onRetry={load} />;
  if (!prefs) return <div className="notice"><h3>Loading</h3><p>Reading your notification settings.</p></div>;

  return (
    <>
      {prefs.discordLinked && (
        <div className="notice blue">
          <h3>You will be mentioned</h3>
          <p>
            You have signed in and linked a Discord account, which are both required. Alerts
            matching your settings below will @-mention you in the shared channel.
          </p>
        </div>
      )}

      {!prefs.discordLinked && (
        <div className="notice">
          <h3>Link Discord to be mentioned</h3>
          <p>
            Operational messages post to the shared channel whether or not you link an account.
            Linking is what lets the worker mention you by name in them.
          </p>
          <p style={{ marginTop: 10 }}>
            <a href={`${BASE}/auth/discord/link`}>Link a Discord account</a>
          </p>
        </div>
      )}

      <div className="notice">
        <h3>Both steps are required, and neither is optional</h3>
        <p>
          You are mentioned only if you have <strong>signed in here at least once</strong> and
          <strong> linked a Discord account</strong>. Signing in is what confirms your
          administrator status; linking is what gives the alert somewhere to point. Doing only
          one leaves you unmentioned — and nothing about a half-finished setup looks broken,
          which is why this says so plainly.
        </p>
      </div>

      <div className="readings">
        <Reading label="Discord account" value={prefs.discordUsername ?? undefined}
          state={prefs.discordLinked ? 'live' : 'unknown'} source="verified OAuth link"
          observedAt={prefs.discordLinked ? new Date(prefs.updatedAt) : undefined}
          reason={prefs.discordLinked ? undefined : 'Not linked, so you cannot be mentioned.'} />
        <Reading label="Personal mentions" value={prefs.mentionsEnabled ? 'On' : 'Off'}
          state="live" source="your settings" observedAt={new Date(prefs.updatedAt)} />
        <Reading label="Quiet hours" value={prefs.quietHoursEnabled ? `${prefs.quietStart}–${prefs.quietEnd}` : 'Off'}
          unit={prefs.quietHoursEnabled ? prefs.timeZone : undefined}
          state="live" source="your settings" observedAt={new Date(prefs.updatedAt)} />
        <Reading label="Settings version" value={String(prefs.version)} state="live"
          source="operations database" observedAt={new Date(prefs.updatedAt)} />
      </div>

      <h2 style={{ margin: '8px 0 18px' }}>Settings</h2>

      <div className="field">
        <label htmlFor="mentions">Mention me in the shared channel</label>
        <div className="row">
          <button id="mentions" className={prefs.mentionsEnabled ? 'action' : 'ghost'}
            aria-pressed={prefs.mentionsEnabled} disabled={saving}
            onClick={() => void patch({ mentionsEnabled: !prefs.mentionsEnabled })}>
            {prefs.mentionsEnabled ? 'On' : 'Off'}
          </button>
        </div>
      </div>

      <div className="field">
        <label htmlFor="quiet">Quiet hours</label>
        <div className="row">
          <button id="quiet" className={prefs.quietHoursEnabled ? 'action' : 'ghost'}
            aria-pressed={prefs.quietHoursEnabled} disabled={saving}
            onClick={() => void patch({ quietHoursEnabled: !prefs.quietHoursEnabled })}>
            {prefs.quietHoursEnabled ? 'On' : 'Off'}
          </button>
          <div>
            <label htmlFor="qs">From</label>
            <input id="qs" type="time" value={prefs.quietStart} disabled={saving}
              onChange={(e) => void patch({ quietStart: e.target.value })} />
          </div>
          <div>
            <label htmlFor="qe">Until</label>
            <input id="qe" type="time" value={prefs.quietEnd} disabled={saving}
              onChange={(e) => void patch({ quietEnd: e.target.value })} />
          </div>
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
          Quiet hours use {prefs.timeZone}. Start and end must differ.
        </p>
      </div>

      <div className="field">
        <label htmlFor="override">Let critical alerts through quiet hours</label>
        <button id="override" className={prefs.criticalOverridesQuietHours ? 'action' : 'ghost'}
          aria-pressed={prefs.criticalOverridesQuietHours} disabled={saving}
          onClick={() => void patch({ criticalOverridesQuietHours: !prefs.criticalOverridesQuietHours })}>
          {prefs.criticalOverridesQuietHours ? 'On' : 'Off'}
        </button>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
          This bypasses quiet hours only. A critical alert for a service you have not selected
          still will not mention you.
        </p>
      </div>

      {prefs.discordLinked && (
        <div className="field">
          <label>Linked account</label>
          <button className="ghost" disabled={saving}
            onClick={async () => { await unlinkDiscord(); void load(); }}>
            Unlink {prefs.discordUsername}
          </button>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
            Unlinking stops mentions. Messages still post to the channel.
          </p>
        </div>
      )}

      {saved && <div className="notice blue"><p>{saved}</p></div>}

      <div className="notice">
        <h3>What these settings cannot do</h3>
        <p>
          They control whether you are mentioned. They do not hide Discord messages, do not
          override your Discord client's own notification settings, and do not stop shared
          operational messages being posted. Suppressed mentions are not replayed later.
        </p>
      </div>
    </>
  );
}

function Failure({ failure, onRetry }: { failure: ApiFailure; onRetry: () => void }) {
  if (failure.kind === 'unauthenticated') return <SignInNotice />;
  // A 503 and a 403 look different on purpose: one is "we cannot tell", the other is "no".
  const cls = failure.kind === 'forbidden' ? 'notice red' : 'notice';
  return (
    <div className={cls}>
      <h3>{failure.kind === 'forbidden' ? 'Not available to this account' : 'Cannot reach the Operations API'}</h3>
      <p>{'reason' in failure ? failure.reason : 'Unknown failure.'}</p>
      {failure.kind !== 'forbidden' && (
        <p style={{ marginTop: 10 }}><button className="ghost" onClick={onRetry}>Try again</button></p>
      )}
    </div>
  );
}
